<script setup>
const props = defineProps({
  title: String,
  subtitle: String,
  fullWidth: Boolean,
  minimumWidth: Boolean,
  customWidth: String,
  allowClickOutsideToClose: {
    type: Boolean,
    default: true
  },
  showCloseButton: {
    type: Boolean,
    default: true
  },
  showHeader: {
    type: Boolean,
    default: true
  },
  ignorePadding: {
    type: Boolean,
    default: false
  }
});

const { transitionColour } = useTailwindConfig();

const isOpen = ref(false);

// If fullWidth, minimumWidth, or customWidth is set, use that width. Otherwise, use default width
const defaultWidth = 'max-w-[1200px] sm:max-w-[1200px]';

// Generate UI overrides based on props
const modalContentClass = computed(() => {
  if (props.fullWidth) return 'w-11/12 max-w-none sm:max-w-none'
  else if (props.minimumWidth) return 'w-auto max-w-[1200px] sm:max-w-[1200px]'
  else if (props.customWidth) return `${props.customWidth} max-w-none`
  else return defaultWidth
})

const modalUI = computed(() => ({
  content: modalContentClass.value,
  body: props.ignorePadding ? 'p-0 sm:p-0' : '',
  overlay: 'bg-black/25',
}))

function open() {
  isOpen.value = true;
}

function close() {
  isOpen.value = false;
}

// Expose open and close functions for parent component
defineExpose({ open, close });
</script>

<template>
  <!-- Ignore the padding if ignorePadding is true -->
  <UModal v-model:open="isOpen" :dismissible="allowClickOutsideToClose" :close="showCloseButton" :ui="modalUI">
    <!-- Header -->
    <template v-if="showHeader" #header>
      <div class="flex w-full justify-between items-start pr-12">
        <div>
          <h3 class="text-xl font-medium">
            {{ title }}
          </h3>
          <p v-if="subtitle" class="text-sm text-gray-500">
            {{ subtitle }}
          </p>
        </div>

        <UButton v-if="showCloseButton" color="neutral" variant="soft"
          class="absolute top-0 right-0 p-0.5 m-2 rounded-lg hover:bg-gray-100" @click="isOpen = false">
          <UIcon name="material-symbols:close-rounded" class="w-7 h-7" />
        </UButton>
      </div>
    </template>

    <!-- Body -->
    <template #body>
      <slot />
    </template>
  </UModal>
</template>